import * as GeographicLib from 'geographiclib';
// A type only: the converter that saves a shape reads buffer settings here,
// and a value import of the model from here would make a loop of the two.
import type {Polygon} from '../models/polygon';
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader.js'
import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp.js'
import BufferParameters from 'jsts/org/locationtech/jts/operation/buffer/BufferParameters.js'
import Area from 'jsts/org/locationtech/jts/algorithm/Area.js'
import {Logger} from '../../common/utils/logger';
import {SphericalUtil} from './spherical-util';

const D2R = Math.PI / 180;

/** Smallest hole kept, in square metres: a smaller ring is the overlay's rounding, not a hole. */
const MIN_HOLE_AREA = 1e-6;

/** Smallest piece kept, in square metres: below it, a piece is the overlay's rounding. */
const MIN_PART_AREA = 0.01;

/** JTS's own default: a sharp corner reaches at most five times the distance before it is cut. */
export const DEFAULT_MITRE_LIMIT = 5;

/**
 * How a corner of the buffer is turned. "Square" used to mean JTS's bevel,
 * which cuts the corner off: a 20 m buffer round a rectangular field came out
 * octagonal. Sharp keeps the corner (JTS's mitre); cut is the bevel, by name.
 */
export type BufferCorner = 'sharp' | 'round' | 'cut';

/**
 * How a line's buffer ends. It went with the corners (round corners, round
 * ends; "square" corners, ends sticking out by the distance; mitre, flat), so
 * a corridor with round bends could not stop flat where the line stops.
 */
export type BufferEnd = 'round' | 'flat' | 'square';

/** A line's buffer on both sides, or on one, as the line was drawn (from its first point). */
export type BufferSide = 'both' | 'left' | 'right';

/** How a buffer is drawn, as chosen when it was made. */
export interface BufferSettings {
  /** Metres from the shape; more than 0. */
  distance: number;
  /** An area's buffer within its outline (a setback) rather than round it. Areas only. */
  inside?: boolean;
  corner: BufferCorner;
  /** Lines only, both sides only (a one-sided buffer ends flat). */
  end?: BufferEnd;
  /** Lines only. */
  side?: BufferSide;
  /** Sharp corners only: how far a corner may reach, in distances, before it is cut. */
  mitreLimit?: number;
}

/**
 * Kept with each shape a buffer made (KEY_BUFFER), so the buffer can be drawn
 * again: when the shape it was drawn round changes, and when it is edited.
 */
export interface BufferParams extends BufferSettings {
  /** The shape it was drawn round. */
  source: string;
  /** The shapes drawn together share this: the first one's id. */
  set: string;
  /** Which piece of the set this is, largest first. */
  part: number;
  /** A ring the buffer leaves inside one of its pieces: a hole of it, not a piece. */
  ring?: boolean;
}

/** A piece of a buffer as rings: its outline, and the holes it leaves inside. */
export interface BufferRings {
  /** The outline, ending on a copy of its first point. */
  outline: google.maps.LatLngLiteral[];
  /**
   * What the buffer closes round without covering -- the bay of a C-shaped
   * area whose mouth it bridges, the yard inside a fence line that closes on
   * itself, a courtyard grown by an inner buffer -- each ending on a copy of
   * its first point.
   */
  holes: google.maps.LatLngLiteral[][];
}

const CORNERS: BufferCorner[] = ['sharp', 'round', 'cut'];
const ENDS: BufferEnd[] = ['round', 'flat', 'square'];
const SIDES: BufferSide[] = ['both', 'left', 'right'];

export class BufferUtil {

  /**
   * The buffer `settings` make round (or within) a shape: its pieces, largest
   * first, or none. An outer buffer is one piece; an inner one can come apart
   * (a dumbbell-shaped plot leaves two), and a one-sided buffer of a line
   * that crosses itself can too.
   */
  static generate(poly: Polygon, settings: BufferSettings): BufferRings[] {
    const distance = Number(settings && settings.distance);
    if (!poly || !(distance > 0) || !Number.isFinite(distance)) {
      return [];
    }
    const area = !!poly.showSurface;
    const outline = area ? (poly.path || []) : (poly.linePath || poly.path || []);
    if (outline.length < 2 || (area && outline.length < 3)) {
      return [];
    }

    // Worked in metres on a plane round the first point, where distances
    // and directions from it are exact (azimuthal equidistant, on WGS84):
    // the buffer is as wide as the app measures, and a shape across the
    // 180th meridian stays one shape. Degrees scaled by a sphere's metre
    // made it up to 0.3% too wide, and raw longitudes put the two sides of
    // that meridian 360 degrees apart.
    const origin = outline[0];
    const geod = GeographicLib.Geodesic.WGS84;
    const toPlane = (p: google.maps.LatLngLiteral): number[] => {
      const r = geod.Inverse(origin.lat, origin.lng, p.lat, p.lng);
      const azimuth = r.azi1 * D2R;
      return [r.s12 * Math.sin(azimuth), r.s12 * Math.cos(azimuth)];
    };
    const toLatLng = (c: {x: number, y: number}): google.maps.LatLngLiteral => {
      const s = Math.hypot(c.x, c.y);
      if (!(s > 0)) {
        return {lat: origin.lat, lng: origin.lng};
      }
      const r = geod.Direct(origin.lat, origin.lng, Math.atan2(c.x, c.y) / D2R, s);
      return {lat: r.lat2, lng: r.lon2};
    };
    // JTS closes every ring; the copy of the first point is made here, so
    // it is exactly that point.
    const ringOf = (coordinates: Array<{x: number, y: number}>): google.maps.LatLngLiteral[] => {
      const ring = coordinates.slice(0, coordinates.length - 1).map(toLatLng);
      if (ring.length) {
        ring.push({...ring[0]});
      }
      return ring;
    };
    const closed = (path: google.maps.LatLngLiteral[]): number[][] => {
      const coords = path.map(toPlane);
      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push([...first]);
      }
      return coords;
    };

    const params = new BufferParameters();
    const corner = CORNERS.includes(settings.corner) ? settings.corner : 'round';
    params.setJoinStyle(corner === 'sharp' ? BufferParameters.JOIN_MITRE
      : corner === 'cut' ? BufferParameters.JOIN_BEVEL : BufferParameters.JOIN_ROUND);
    const mitreLimit = Number(settings.mitreLimit);
    params.setMitreLimit(mitreLimit >= 1 ? mitreLimit : DEFAULT_MITRE_LIMIT);

    let geoInput;
    let signed = distance;
    if (area) {
      // Within the outline, the shape's own holes count: a setback is kept
      // from a courtyard's walls as from the boundary. Round it, they are
      // inside the shape the buffer is measured from, and do not.
      const rings = [closed(outline)];
      if (settings.inside) {
        signed = -distance;
        (poly.holes || [])
          .filter(hole => hole && hole !== poly && hole.isHole && (hole.path || []).length >= 3)
          .forEach(hole => rings.push(closed(hole.path)));
      }
      geoInput = {type: 'Polygon', coordinates: rings};
    } else {
      const side = SIDES.includes(settings.side) ? settings.side : 'both';
      if (side !== 'both') {
        // JTS puts a one-sided buffer on the left for a positive distance.
        params.setSingleSided(true);
        signed = side === 'left' ? distance : -distance;
      } else {
        const end = ENDS.includes(settings.end) ? settings.end : 'round';
        params.setEndCapStyle(end === 'flat' ? BufferParameters.CAP_FLAT
          : end === 'square' ? BufferParameters.CAP_SQUARE : BufferParameters.CAP_ROUND);
      }
      geoInput = {type: 'LineString', coordinates: outline.map(toPlane)};
    }

    let geometry;
    try {
      geometry = new GeoJSONReader().read(geoInput);
    } catch (e) {
      // Too few points for a ring or a line: nothing to buffer.
      Logger.error('BufferUtil: the shape could not be read', e && e.message);
      return [];
    }
    let buffered;
    try {
      buffered = BufferOp.bufferOp(geometry, signed, params);
    } catch (e) {
      // Self-intersecting / invalid geometry — clean with buffer(0) and retry.
      Logger.log('BufferUtil: bufferOp failed, retrying cleaned', e && e.message);
      try {
        // Modular jsts geometries have no .buffer(): use the static op.
        geometry = BufferOp.bufferOp(geometry, 0);
        buffered = BufferOp.bufferOp(geometry, signed, params);
      } catch (e2) {
        Logger.error('BufferUtil: bufferOp failed after cleaning', e2 && e2.message);
        return [];
      }
    }
    if (!buffered || buffered.isEmpty()) {
      return [];
    }

    // Each piece of real size, largest first; slivers are the overlay's
    // rounding. One outline used to be all a buffer could be, so a buffer
    // that came apart was refused outright and Create closed doing nothing.
    const parts = BufferUtil.polygonParts(buffered).sort((a, b) => b.getArea() - a.getArea());
    const total = parts.reduce((sum, part) => sum + part.getArea(), 0);
    return parts
      .filter(part => part.getArea() > Math.max(MIN_PART_AREA, total * 1e-6))
      .map(part => {
        // The outline and each inner ring apart: all of them in one list drew
        // the outline, a seam to the inner ring, round it and back along the
        // seam, and the perimeter counted the inner ring and the seam twice.
        const holes: google.maps.LatLngLiteral[][] = [];
        for (let i = 0; i < part.getNumInteriorRing(); i++) {
          const coordinates = part.getInteriorRingN(i).getCoordinates();
          if (Area.ofRing(coordinates) > MIN_HOLE_AREA) {
            holes.push(ringOf(coordinates));
          }
        }
        return {outline: ringOf(part.getExteriorRing().getCoordinates()), holes};
      })
      .filter(piece => piece.outline.length > 3);
  }

  /** The ground a piece covers, in square metres: its outline less its holes. */
  static areaOf(piece: BufferRings): number {
    const holes = piece.holes.reduce((sum, hole) => sum + SphericalUtil.computeArea(hole), 0);
    return Math.max(0, SphericalUtil.computeArea(piece.outline) - holes);
  }

  /** Settings read back from a stored shape, or null when there are none to trust. */
  static paramsFromData(data: any): BufferParams | null {
    if (!data || typeof data !== 'object') {
      return null;
    }
    const distance = Number(data.distance);
    if (!(distance > 0) || !Number.isFinite(distance) || typeof data.source !== 'string' || !data.source) {
      return null;
    }
    const params: BufferParams = {
      source: data.source,
      set: typeof data.set === 'string' && data.set ? data.set : '',
      part: Number.isInteger(data.part) && data.part >= 0 ? data.part : 0,
      distance,
      corner: CORNERS.includes(data.corner) ? data.corner : 'round',
    };
    if (data.inside === true) {
      params.inside = true;
    }
    if (ENDS.includes(data.end)) {
      params.end = data.end;
    }
    if (SIDES.includes(data.side)) {
      params.side = data.side;
    }
    if (Number(data.mitreLimit) >= 1) {
      params.mitreLimit = Number(data.mitreLimit);
    }
    if (data.ring === true) {
      params.ring = true;
    }
    return params;
  }

  /** The stored form: only what is set, so a Firestore document holds no undefined. */
  static paramsToData(params: BufferParams): any {
    const data: any = {source: params.source, set: params.set, part: params.part,
      distance: params.distance, corner: params.corner};
    ['inside', 'end', 'side', 'mitreLimit', 'ring'].forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== false) {
        data[key] = params[key];
      }
    });
    return data;
  }

  /** Whether two settings draw the same buffer. */
  static sameSettings(a: BufferSettings, b: BufferSettings): boolean {
    return !!a && !!b && a.distance === b.distance && !!a.inside === !!b.inside && a.corner === b.corner
      && (a.end || '') === (b.end || '') && (a.side || '') === (b.side || '')
      && (a.mitreLimit || 0) === (b.mitreLimit || 0);
  }

  /** The Polygon parts of a (possibly Multi) geometry. */
  private static polygonParts(geom: any): any[] {
    if (geom.getGeometryType() === 'Polygon') {
      return geom.isEmpty() ? [] : [geom];
    }
    const parts = [];
    for (let i = 0; i < geom.getNumGeometries(); i++) {
      const g = geom.getGeometryN(i);
      if (g && g.getGeometryType() === 'Polygon' && !g.isEmpty()) {
        parts.push(g);
      }
    }
    return parts;
  }
}
